<template>
	<div class="bg-white">
		<div v-if="loading" layout="row" class="bg-white" style="height: 100%" layout-align="center center">
				<v-progress-circular indeterminate color="secondary" size="64"></v-progress-circular>
		</div>
		<div v-else-if="completed" layout="column" layout-align="center center" class="full-width">
			<div layout="row" layout-align="start center" class="title grey--text text-uppercase pr2-pc pl2-pc pr1-mobile pl1-mobile font-weight-medium mb2-pc mb1-mobile mt2-pc mt1-mobile full-width">{{$t('surveys.the_survey')}}
				<span flex></span>
				<v-btn @click="$router.push({name: 'surveys-show-survey'})" v-if="preview" :disabled="!editable" small color="secondary" depressed><v-icon class="mr-2">mdi-pencil</v-icon>{{$t('_.edit')}}</v-btn>
			</div>
			<div layout="row" layout-align="center center" class="headline full-width mt2-mobile font-weight-bold">
				{{i18n(survey.title)}}
			</div>
			<div layout="row" layout-align="start start" class="field-width width-95-mobile mt-4">
				<div style="white-space: pre-wrap; word-wrap: break-word;font-family: inherit;" class="mt-2 pa-1">{{i18n(survey.thanks)}}</div>
			</div>
			<div layout="row" layout-align="center center" class="field-width mt-5 mb-3 width-95-mobile">
				<v-btn v-if="preview" @click="$router.push({name: 'surveys-show-survey'})" :disabled="!editable" color="secondary" depressed outline><v-icon class="mr-2">mdi-pencil-outline</v-icon>{{$t('surveys.back_edit')}}</v-btn>
				<v-btn v-else @click="$router.push({name: 'my-wall'})"><v-icon class="mr-2">mdi-home</v-icon>{{$t('surveys.back_wall')}}</v-btn>
			</div>
		</div>
		<div v-else layout="column" layout-align="start start" class="full-width pr2-pc pl2-pc pb-2 pr1-mobile pl1-mobile" flex>
			<div layout="row" layout-align="start center" class="title grey--text text-uppercase font-weight-medium mb2-pc mb1-mobile mt2-pc mt1-mobile full-width">{{$t('surveys.the_survey')}}
				<span flex></span>
				<v-btn @click="$router.push({name: 'surveys-show-survey'})" v-if="preview" :disabled="!editable" color="secondary" small depressed><v-icon class="mr-2">mdi-pencil</v-icon>{{$t('_.edit')}}</v-btn>
			</div>
			<div layout="row" layout-align="center center" class="headline full-width font-weight-bold mt2-mobile">
				{{i18n(survey.title)}}
			</div>
			<div layout="row" layout-align="center center" class="full-width mt-4 mt0-mobile">
				{{i18n(survey.description)}}
			</div>
			<div v-if="nbPages != 1" layout="column" layout-align="center center" class="full-width hidden-sm-and-down">
				<div layout="row" layout-align="center center" class="field-width mb-5 mt-4">
					<div layout="row" layout-align="start center" v-for="(p,i) in survey.pages" :key="'page_' + p.id">
						<v-divider :style="'width:' + dividerWidth + 'px'"></v-divider>
						<v-btn @click="currentPage = p.number" depressed :color="(currentPage == p.number) ? 'secondary' : '#E0E0E0'" style="width: 80px; height: 80px; font-size: 30px" fab class="mx-0">{{p.number}}</v-btn>
						<v-divider v-if="survey.pages.length == (i + 1)" :style="'width:' + dividerWidth + 'px'"></v-divider>
					</div>
				</div>
			</div>

			<div v-if="nbPages && survey.pages[currentPage - 1] && survey.pages[currentPage - 1].title" layout="row" layout-align="center center" class="title mt3-mobile full-width font-weight-bold">
				{{i18n(survey.pages[currentPage - 1].title)}}
			</div>
			<div v-if="nbPages && nbPages > 1" layout="row" layout-align="center center" class="full-width mt-1">
				{{totalQuestionsPast()}} / {{totalQuestions()}}
			</div>

			<div layout="column" layout-align="center center" class="full-width mt-4">
				<v-form v-if="survey.pages && survey.pages[currentPage - 1] && survey.pages[currentPage - 1].questions" ref="surveyForm" class="field-width">
					<div  v-for="q in survey.pages[currentPage - 1].questions" :key="q.id" layout="column" layout-align="start start" class="pa-1 full-width mb-2">
						<div layout="row" layout-align="start start" class="full-width">
							<div layout="column" layout-align="center start">
								<div class="font-weight-bold mb-2">{{q.number}}. {{i18n(q.label)}} <span v-if="q.is_required" class="orange--text">*</span></div>
							</div>
						</div>
						<v-textarea v-if="q.type == textarea" v-model="q.value" :placeholder="i18n(q.description)" :rules="[ v => (!q.is_required || !!v || preview) || $t('errors.required') ]" :readonly="preview" class="full-width py-0 my-0" flex></v-textarea>
						<v-text-field v-else-if="q.type == text" v-model="q.value" :placeholder="i18n(q.description)" :readonly="preview" :rules="[ v => (!q.is_required || !!v || preview) || $t('errors.required') ]" class="full-width py-0 my-0" flex></v-text-field>
						<div v-else-if="q.type == checkbox" class="full-width">
							<v-checkbox v-for="o in q.options" :label="i18n(o.label)" :value="o.id"
							v-model="q.options_ids[o.position]" :readonly="preview" :rules="[ v => (!q.is_required || preview || checkboxValidator(q.options_ids, q)) || $t('errors.required') ]" :key="`optioncb_${o.id}`" hide-details class="mt-0 pt-0"></v-checkbox>

							<div v-if="q.is_required && !preview && invalids[q.id]" class="v-messages theme--light error--text"><div class="v-messages__wrapper"><div class="v-messages__message">{{$t('errors.required')}}</div></div></div>
						</div>
						<v-radio-group v-else v-model="q.option_id" :readonly="preview" :rules="[ v => (!q.is_required || !!v || preview) || $t('errors.required') ]" class="full-width py-0 my-0">
							<v-radio v-for="o in q.options" :key="`option_${o.id}`" :label="i18n(o.label)" :value="o.id"></v-radio>
						</v-radio-group>
					</div>
				</v-form>
			</div>

			<div layout="column" layout-align="center center" class="full-width mt-5 mb-5">
				<div layout="row" layout-align="start center" class="field-width width-95-mobile">
						<v-btn v-if="currentPage != 1" depressed color="#E0E0E0" @click="currentPage--" style="text-transform: none"><v-icon class="mr-1">mdi-chevron-left</v-icon>{{$t('surveys.prev')}}</v-btn>
						<span flex></span>
						<v-btn v-if="currentPage != survey.pages.length" depressed color="secondary" @click="checkForm() && currentPage++" style="text-transform: none">{{$t('surveys.continue')}}</v-btn>
						<v-btn v-else depressed color="secondary" @click="save" style="text-transform: none">{{$t('surveys.finish')}}</v-btn>
				</div>
				<div layout="row" layout-align="end center" class="field-width width-95-mobile">
						<span class="orange--text">*</span> {{$t('surveys.mandatory')}}
				</div>
			</div>
		</div>
	</div>
</template>
<script>
    import Consts from '@/consts'
    import I18nMixin from '@/mixins/i18n'
		import Surveys from '@/api/surveys'
		import moment from 'moment'
		import Auth from '@/plugins/auth'

    export default {
				components: {

        },
        mixins: [I18nMixin],
        props: {
            preview: {
                required: false,
                type: Boolean,
                default: false
            }
        },
        data () {
            return {
							currentPage: 1,
							loading: false,
							survey: {
									message: {
											levels: [],
											sectors: [],
											schools: [],
											cities: [],
											regions: [],
											regional_directorates: [],
									},
									title: {},
									creator: {},
									description: {},
									thanks: {},
									pages: {}
							},
							completed: false,
							invalids: {},
							pagesCheck: {}
            }
        },
        mounted () {
					if(!this.preview){
						this.submittable();
					}

					this.fetch();
        },
        methods: {
					totalQuestions(){
						let n = 0;
						for(let p in this.survey.pages){
							if(p != '+'){
								n += this.survey.pages[p].questions.length;
							}
						}

						return n;
					},
					totalQuestionsPast(){
						let n = 0;
						for(let p in this.survey.pages){
							if(p < this.currentPage && p != '+'){
								n += this.survey.pages[p].questions.length;
							}
						}

						return n;
					},
					submittable(){
						Surveys.submittable(this.$route.params.id).then(() => {
							//Can access the form
						}, () => {
							//Nop, redirect to the wall
							this.$router.push({name: 'my-wall'});
						});
					},
					fetch(){
						this.loading = true;

						Surveys.get(this.$route.params.id).then(res => {
							this.survey = res.data;
							if(!this.survey.pages || !this.survey.pages.length){
								this.survey.pages = [];
								this.survey.pages.push({
									questions: this.survey.questions
								});
							}
							this.loading = false;

							//Champs de réponses par défaut
							for(let i = 0; i < this.survey.pages.length; i++){
								for(let j = 0; j < this.survey.pages[i].questions.length; j++){
									this.survey.pages[i].questions[j].options_ids = [];
									this.survey.pages[i].questions[j].option_id = null;
									this.survey.pages[i].questions[j].value = null;
									this.survey.pages[i].questions[j] = Object.assign({}, this.survey.pages[i].questions[j]);
								}
							}
						});
					},
					questionTypeLabel(t){
							switch (t) {
									case Consts.RADIO:
											return this.$t('surveys.choices_list');
									case Consts.CHECKBOX:
											return this.$t('surveys.choices_list_mul');
									case Consts.TEXT:
											return this.$t('surveys.short_answer');
									case Consts.TEXTAREA:
											return this.$t('surveys.long_answer');
							}
					},
					checkboxValidator(arr, q){
						if(!arr || !arr.length){
							if(this.pagesCheck[this.currentPage]){
								this.invalids[q.id] = true;
								this.invalids = Object.assign({}, this.invalids);
							}
							return false;
						} else{
							let has = false;
							for(let i = 0; i < arr.length; i++){
								if(arr[i]){
									has = true;
									break;
								}
							}
							if(this.pagesCheck[this.currentPage]){
								this.invalids[q.id] = !has;
								this.invalids = Object.assign({}, this.invalids);
							}
							return has;
						}
					},
					checkForm(){
						this.pagesCheck[this.currentPage] = true;
						if (!this.preview && !this.$refs['surveyForm'].validate()) {
								return false;
						}
						return true;
					},
					save(){
						if(!this.checkForm()){
							return;
						}

						if(this.preview){
							this.completed = true;
							return;
						}

						this.loading = true;

						Surveys.submit(this.survey.id, this.survey).then(() => {
							this.loading = false;
							this.completed = true;
						}, () => {
							this.loading = false;
						})
					}
        },
        computed: {
					questionTypes () {
							return [
									{ value: Consts.RADIO, text: this.$t('surveys.choices_list'), icon: 'format-list-bulleted' },
									{ value: Consts.CHECKBOX, text: this.$t('surveys.choices_list_mul'), icon: 'format-list-bulleted-square' },
									{ value: Consts.TEXT, text: this.$t('surveys.short_answer'), icon: 'format-text-variant' },
									{ value: Consts.TEXTAREA, text: this.$t('surveys.long_answer'), icon: 'format-letter-case' },
							]
					},
					text(){
						return Consts.TEXT;
					},
					textarea(){
						return Consts.TEXTAREA;
					},
					checkbox(){
						return Consts.CHECKBOX;
					},
					radio(){
						return Consts.RADIO;
					},
					nbPages(){
						return this.survey.pages.length;
					},
					dividerWidth (){
						return (900 - (80 * this.nbPages)) / (this.nbPages + 1);
					},
					isCurrentLevel(){
						if(Auth.user.level == this.survey.creator.level){
								return true;
						}
						return false;
					},
					editable(){
							if(!this.isCurrentLevel || (this.survey.message.is_enabled && moment().isSameOrAfter(this.survey.message.date_start) && moment().isSameOrBefore(this.survey.message.date_end))){
									return false;
							}
							return true;
					},
        }
    }
</script>
<style>
</style>
<style scoped>
.question-type:hover {
  background-color: #f5f5f5;
}
.message-btn {
  height: 14px;
  width: 14px;
  border: 1px;
  border-radius: 50% 50%;
}
</style>